import React, {useState} from "react"
import styled from "styled-components"

import tracking from "@irati-tracking/recognition/recognition"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import authentication from "@irati/auth"
import Validation from "@irati/utils/validation"
import qs from "qs"
import RecognitionService from "@irati/recognition/services/Recognition"
import {GroupCelebration} from "@irati/types"
import {H2, Label} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {Recognition as RecognitionField} from "@irati-web/fields/poll/votes"
import Spacing from "@irati/ui/src/Spacing"
import {Row} from "react-bootstrap"
import {ValidationButton} from "@irati/ui/src/FormItem"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import ProfilePicGroup, {DEFAULT_MULTIPLE_PICTURE_SIZE, DEFAULT_SINGLE_PICTURE_SIZE} from "../../ProfilePicGroup"
import {CelebrationIcon, ProfilePicContainer} from "../../Polls/ClosedPoll/styled"
import ScheduledDate from "../shared/layouts/ScheduledDate"
import Comment from "../shared/Comments/Comment"
import LineThroughText from "../shared/layouts/LineThroughText"
import {getGroupCelebrationPath} from "../../../utils/paths"

interface Props extends InjectIntl {
  id?: string
  groupCelebration: GroupCelebration
  onMessageEdited: () => void
  onMessageCreated: () => void
}
const Container = styled(Flex)`
  padding-top: ${({theme}) => theme.spacing.nine};
  flex-direction: column;
`
const Ribbon = styled.img`
  width: 100%;
  position: absolute;
  top: ${({theme}) => theme.spacing.four};
  left: 0;
`

const ViewGroupCelebrationMessage: React.FC<Props> = (props) => {
  const {groupCelebration, formatMessage, onMessageCreated, onMessageEdited} = props
  const query = qs.parse(window.location.search.replace("?", ""))
  const userComment = groupCelebration.messages?.find((item) => item.createdBy?.uuid === authentication.user().uuid)
  const [editMode, setEditMode] = useState(query.edit === "true" || !userComment)
  const [message, setMessage] = useState(userComment?.message)
  const [submit, setSubmit] = useState()
  const greetingLabel = `${groupCelebration?.greeting?.label}, ${groupCelebration?.receiver.firstName} ${groupCelebration?.receiver.lastName}`
  const messageBtnKey =
    editMode && userComment
      ? "platform.button.save"
      : userComment
      ? "recognition.groupCelebration.actions.editMessage"
      : "recognition.groupCelebration.actions.addMessage"
  const otherUsersComments = groupCelebration.messages?.filter((item) => item.createdBy?.uuid !== authentication.user().uuid)
  const otherUsersCommentsHeading = otherUsersComments?.length
    ? "recognition.popup.comments.otherUsers"
    : "recognition.popup.nocomments.otherUsers"
  const onMessageAdded = () => {
    tracking.launchGroupCelebration(groupCelebration, groupCelebration.rewardAmount, "greeting_card_add_message")
    onMessageCreated()
  }
  const onMessageBtnClick = () => {
    if (!editMode) {
      setEditMode(true)
      return
    }
    const args = {
      groupCelebrationID: groupCelebration.uuid,
      message,
      messageId: userComment?.uuid
    }
    if (!userComment) {
      tracking.recognitionCtaClick({
        source: "add_message_modal",
        type: "add_message"
      })
    }
    const promise = userComment ? RecognitionService.updateGroupCelebrationMessage : RecognitionService.createGroupCelebrationMessage
    const cb = userComment ? onMessageEdited : onMessageAdded
    const submitPromise = promise(args)
      .then(cb)
      .finally(() => setSubmit())
    setSubmit(submitPromise)
  }
  const onInviteOthers = () => {
    tracking.recognitionCtaClick({
      source: "add_message_modal",
      type: "invite_others"
    })
  }
  return (
    <Container>
      <Ribbon src={groupCelebration.greeting?.image?.href!} />
      <Flex justifyContent="center">
        <ProfilePicContainer>
          <CelebrationIcon src={groupCelebration?.greeting?.icon?.href} />
          <ProfilePicGroup
            users={[groupCelebration?.receiver]}
            outerWrapperSize={DEFAULT_MULTIPLE_PICTURE_SIZE}
            singlePictureSize={DEFAULT_SINGLE_PICTURE_SIZE}
          />
        </ProfilePicContainer>
      </Flex>
      <Label className="center" small>
        <FormattedMessage id="recognition.popup.groupCelebration.headerType" />
      </Label>
      <H2 textAlign="center">
        <FormattedMessage id={greetingLabel} />
      </H2>
      <ScheduledDate scheduledGroupCelebration={groupCelebration} />
      <Spacing marginTop={3}>
        {(editMode || !userComment) && (
          <Row>
            <RecognitionField
              onChange={setMessage}
              value={message}
              maxLength={2000}
              validation={Validation.string(formatMessage({id: "platform.defaultValidation.required"}))}
              placeholder={formatMessage({id: "recognition.popup.shared.comments.placeholder"})}
            />
          </Row>
        )}
        {!editMode && userComment && <Comment comment={userComment} hideActions hideTime />}
        <ValidationButton
          primary
          small={!editMode}
          load={submit}
          full={!(!editMode && userComment)}
          marginTop={2}
          onClick={onMessageBtnClick}>
          <FormattedMessage id={messageBtnKey} />
        </ValidationButton>
      </Spacing>
      <Spacing className="center" marginTop={3}>
        <LineThroughText text={formatMessage({id: otherUsersCommentsHeading})} />
      </Spacing>
      {otherUsersComments?.length > 0 && (
        <Flex direction="column" gap={2}>
          {otherUsersComments?.map((colleagueMessage) => (
            <Comment comment={colleagueMessage} hideActions hideTime />
          ))}
        </Flex>
      )}
      <Flex>
        <ButtonRouterLink
          small
          onClick={onInviteOthers}
          marginTop={2}
          scrollToTop={false}
          plain
          to={`${getGroupCelebrationPath(groupCelebration)}/invite`}>
          <FormattedMessage id="recognition.groupCelebration.actions.inviteOthers" />
        </ButtonRouterLink>
      </Flex>
    </Container>
  )
}

export default injectIntl(ViewGroupCelebrationMessage)
